import React, { useEffect, useState, useRef } from 'react';
import { fixHtmlImagePaths, processEmbedUrls } from "../utils/imageUtils";
import { Link, useParams } from 'react-router-dom';
import EventService from '../services/EventService';
import { normalizeImageUrl } from '../utils/imageUtils';
import dayjs from 'dayjs';
import { useData } from '../context/DataContext';
import { updateMetaTags } from '../utils/seoUtils';
import useTikTokEmbed from '../utils/useTikTokEmbed';
import ReviewSection from '../components/ReviewSection';

const EventDetailPage = () => {
    const { slug } = useParams();
    const { settings } = useData();
    const [event, setEvent] = useState(null);
    const [otherEvents, setOtherEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    const lastFetchedSlugRef = useRef(null);
    const isFetchingRef = useRef(false);
    const contentRef = useRef(null);

    useTikTokEmbed(contentRef, [event]);

    useEffect(() => {
        if (event) {
            updateMetaTags({
                title: event.title,
                description: event.description || event.shortDescription || event.title,
                image: event.imageUrl,
            });
        }
    }, [event]);

    useEffect(() => {
        const fetchEvent = async () => {
            if (isFetchingRef.current) return;
            if (lastFetchedSlugRef.current === slug) return;
            
            isFetchingRef.current = true;
            setLoading(true);
            try {
                const [eventRes, allEventsRes] = await Promise.all([
                    EventService.getBySlug(slug),
                    EventService.getAll()
                ]);
                
                const currentEvent = eventRes?.data || eventRes;
                setEvent(currentEvent);
                lastFetchedSlugRef.current = slug;

                let allData = [];
                if (Array.isArray(allEventsRes)) {
                    allData = allEventsRes;
                } else if (Array.isArray(allEventsRes?.data)) {
                    allData = allEventsRes.data;
                }
                
                const currentId = currentEvent?._id || currentEvent?.id;
                const others = allData
                    .filter(e => (e._id || e.id) !== currentId && (e.isActive !== false))
                    .slice(0, 5);
                setOtherEvents(others);
            } catch (err) {
                console.error('Error fetching event data:', err);
            } finally {
                setLoading(false);
                isFetchingRef.current = false;
            }
        };
        fetchEvent();
        window.scrollTo(0, 0);
    }, [slug]);

    if (loading) {
        return (
            <main style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="text-center">
                    <div className="spinner-border text-danger" role="status" />
                    <p className="mt-3 text-muted">Đang tải chi tiết sự kiện...</p>
                </div>
            </main>
        );
    }

    if (!event) {
        return (
            <main style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="text-center">
                    <p className="text-muted">Không tìm thấy sự kiện này hoặc đã kết thúc.</p>
                    <Link to="/su-kien" style={{ color: '#ff0000', fontWeight: 700 }}>← Quay lại danh sách sự kiện</Link>
                </div>
            </main>
        );
    }

    return (
        <main style={{ paddingBottom: 60, background: '#fcfcfc' }}>
            <div className="py-4 mt-[50px]">
                <div className="container px-4 text-left">
                    <nav aria-label="breadcrumb">
                        <ol className="flex items-center gap-2 text-xs md:text-sm uppercase tracking-wider mb-0 p-0 list-none">
                            <li className="breadcrumb-item"><Link to="/" className="hover:text-red-600 transition-colors no-underline text-slate-500">Trang chủ</Link></li>
                            <span className="opacity-30">/</span>
                            <li className="breadcrumb-item"><Link to="/su-kien" className="hover:text-red-600 transition-colors no-underline text-slate-500">Sự Kiện</Link></li>
                            <span className="opacity-30">/</span>
                            <li className="breadcrumb-item active text-slate-900 font-bold" aria-current="page">{event.title}</li>
                        </ol>
                    </nav>
                </div>
            </div>

            <div className="container">
                <div className="row g-4">
                    <div className="col-lg-3 d-none d-lg-block">
                        <div style={{ position: 'sticky', top: 100 }}>
                            <div className="bg-white rounded-4 shadow-sm p-4 border border-slate-100">
                                <h3 style={{ fontSize: 18, fontWeight: 800, color: '#111', marginBottom: 20, paddingBottom: 12, borderBottom: '2px solid #ff0000' }}>
                                    Sự kiện khác
                                </h3>
                                
                                <div className="space-y-4" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    {otherEvents.length > 0 ? otherEvents.map(item => (
                                        <Link 
                                            key={item._id || item.id} 
                                            to={`/su-kien/${item.slug}`}
                                            style={{ textDecoration: 'none', display: 'flex', gap: 12, alignItems: 'start' }}
                                        >
                                            <div style={{ width: 80, height: 60, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: '#f5f5f5' }}>
                                                {item.imageUrl ? (
                                                    <img src={normalizeImageUrl(item.imageUrl)} className="w-100 h-100" style={{ objectFit: 'cover' }} alt={item.title} />
                                                ) : (
                                                    <div className="w-100 h-100 d-flex align-items-center justify-center text-slate-300 bg-slate-100">
                                                        <i className="bi bi-calendar"></i>
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <h4 style={{ fontSize: 13, fontWeight: 700, color: '#333', lineHeight: 1.4, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {item.title}
                                                </h4>
                                                <p style={{ fontSize: 11, color: '#999', margin: '4px 0 0' }}>
                                                    {dayjs(item.eventStartDate).format('DD/MM/YYYY')}
                                                </p>
                                            </div>
                                        </Link>
                                    )) : (
                                        <p className="text-muted text-sm italic">Không có sự kiện khác.</p>
                                    )}
                                </div>

                                <Link to="/su-kien" className="d-block text-center mt-4" style={{ fontSize: 13, color: '#ff0000', fontWeight: 700, textDecoration: 'none' }}>
                                    Xem tất cả sự kiện →
                                </Link>
                            </div>
                            
                            <div className="mt-4 bg-black rounded-4 p-4 text-white shadow-md text-center">
                                <h4 style={{ fontSize: 16, fontWeight: 800, marginBottom: 12 }}>Bạn cần hỗ trợ?</h4>
                                <p style={{ fontSize: 13, opacity: 0.9, marginBottom: 16 }}>Liên hệ trực tiếp với chúng tôi để được tư vấn nhanh nhất.</p>
                                <a 
                                    href={`tel:${settings?.hotline?.replace(/[^0-9]/g, '')}`} 
                                    className="btn btn-light w-100" 
                                    style={{ color: '#ff0000', fontWeight: 800, borderRadius: 10 }}
                                >
                                    Gọi ngay: {settings?.hotline}
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-lg-9">
                        <div className="bg-white rounded-4 shadow-sm p-4 p-md-5 border border-slate-100">
                            <div className="mb-4">
                                <h1 style={{ fontSize: 24, fontWeight: 800, color: '#111', lineHeight: 1.3, marginBottom: 16 }}>
                                    {event.title}
                                </h1>
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 8, 
                                        color: '#666', 
                                        fontSize: 13,
                                        background: '#f8f9fa',
                                        padding: '6px 14px',
                                        borderRadius: 10,
                                        border: '1px solid #eee'
                                    }}>
                                        <i className="bi bi-calendar-check text-danger"></i>
                                        <span className="font-semibold text-secondary">Thời gian:</span>
                                        <strong>{dayjs(event.eventStartDate).format('DD/MM/YYYY HH:mm')}</strong>
                                        <span style={{ margin: '0 4px', opacity: 0.5 }}>—</span> 
                                        <strong>{dayjs(event.eventEndDate).format('DD/MM/YYYY HH:mm')}</strong>
                                    </div>
                                    
                                    {(() => {
                                        const now = dayjs();
                                        const start = dayjs(event.eventStartDate);
                                        const end = dayjs(event.eventEndDate);
                                        
                                        if (now.isBefore(start)) {
                                            return (
                                                <div style={{ fontSize: 13, color: '#f59e0b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }}></span>
                                                    Sắp diễn ra
                                                </div>
                                            );
                                        } else if (now.isAfter(end)) {
                                            return (
                                                <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#94a3b8' }}></span>
                                                    Đã kết thúc
                                                </div>
                                            );
                                        } else {
                                            return (
                                                <div style={{ fontSize: 13, color: '#22c55e', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }}></span>
                                                    Đang diễn ra
                                                </div>
                                            );
                                        }
                                    })()}
                                </div>
                            </div>

                            {event.imageUrl && (
                                <div style={{ 
                                    borderRadius: 16, 
                                    overflow: 'hidden', 
                                    marginBottom: 32, 
                                    boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
                                    border: '1px solid #f0f0f0'
                                }}>
                                    <img
                                        src={normalizeImageUrl(event.imageUrl)}
                                        alt={event.title}
                                        style={{ width: '100%', maxHeight: 450, objectFit: 'cover' }}
                                    />
                                </div>
                            )}

                            <div
                                ref={contentRef}
                                className="ql-content event-detail-content"
                                style={{ 
                                    fontSize: 15, 
                                    color: '#333', 
                                    lineHeight: 1.8,
                                    maxWidth: '100%',
                                    overflowX: 'hidden'
                                }}
                            >
                                {event.content ? (
                                    <div dangerouslySetInnerHTML={{ __html: processEmbedUrls(fixHtmlImagePaths(event.content?.replace(/&nbsp;/g, ' '))) }} />
                                ) : (
                                    <p className="text-muted italic text-center py-5">Nội dung sự kiện đang được cập nhật...</p>
                                )}
                            </div>

                            <div style={{ 
                                marginTop: 40, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                paddingTop: 30,
                                borderTop: '1px solid #f0f0f0'
                            }}>
                                <Link
                                    to="/su-kien"
                                    style={{ 
                                        display: 'inline-flex', 
                                        alignItems: 'center', 
                                        gap: 8, 
                                        color: '#666', 
                                        fontWeight: 600, 
                                        textDecoration: 'none', 
                                        fontSize: 14,
                                        transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.color = '#ff0000'}
                                    onMouseLeave={e => e.currentTarget.style.color = '#666'}
                                >
                                    <i className="bi bi-arrow-left"></i> Quay lại
                                </Link>
                                
                                <Link 
                                    to="/lien-he"
                                    style={{
                                        background: '#ff0000',
                                        color: '#fff',
                                        padding: '10px 24px',
                                        borderRadius: 10,
                                        fontWeight: 700,
                                        textDecoration: 'none',
                                        fontSize: 14,
                                        boxShadow: '0 4px 12px rgba(255, 0, 0, 0.3)',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.background = '#e57218';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.background = '#ff0000';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    Đăng ký tham gia
                                </Link>
                            </div>

                            {/* Review & Rating Section */}
                            <ReviewSection
                                targetType="event"
                                targetId={slug || event?._id}
                                targetTitle={event?.title}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default EventDetailPage;
